import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Row, Col, Badge, Table, Tab, Tabs, Alert } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';

const EmployeeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  const [employee, setEmployee] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  
  // Fetch employee data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        // Fetch employee details
        const response = await fetch(`/api/employees/${id}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch employee details');
        }
        
        const data = await response.json();
        setEmployee(data);
        
        // Fetch time entries for this employee
        const timeResponse = await fetch(`/api/time-entries/employee/${id}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (timeResponse.ok) {
          const timeData = await timeResponse.json();
          setTimeEntries(timeData);
        }
        
        // Get projects this employee is assigned to
        const projectsQuery = new URLSearchParams();
        projectsQuery.append('employeeId', id);
        
        const projectsResponse = await fetch(`/api/projects?employeeId=${id}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (projectsResponse.ok) {
          const projectsData = await projectsResponse.json();
          setProjects(projectsData);
        }
      } catch (err) {
        console.error('Error fetching employee details:', err);
        setError('Failed to load employee details');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEmployeeData();
  }, [id, token]);
  
  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${parseFloat(value).toFixed(2)}`;
  };
  
  // Calculate total hours and earnings
  const calculateTotals = () => {
    if (!timeEntries || timeEntries.length === 0 || !employee) {
      return { regularHours: 0, overtimeHours: 0, totalHours: 0, totalEarnings: 0 };
    }
    
    const regularHours = timeEntries.reduce((sum, entry) => sum + parseFloat(entry.RegularHours || 0), 0);
    const overtimeHours = timeEntries.reduce((sum, entry) => sum + parseFloat(entry.OvertimeHours || 0), 0);
    const totalHours = regularHours + overtimeHours;
    
    const regularEarnings = regularHours * parseFloat(employee.HourlyWage);
    const overtimeEarnings = overtimeHours * parseFloat(employee.HourlyWage) * 1.5;
    const totalEarnings = regularEarnings + overtimeEarnings;
    
    return {
      regularHours,
      overtimeHours,
      totalHours,
      regularEarnings,
      overtimeEarnings,
      totalEarnings
    };
  };
  
  // Handle deletion
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this employee? This cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/employees/${id}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.msg || 'Failed to delete employee');
      }
      
      toast.success('Employee deleted successfully');
      navigate('/employees');
    } catch (err) {
      console.error('Error deleting employee:', err);
      toast.error(err.message || 'Failed to delete employee');
    }
  };
  
  // Show loading state
  if (isLoading) {
    return <div className="text-center mt-5">Loading employee details...</div>;
  }
  
  // Show error state
  if (error) {
    return (
      <Alert variant="danger" className="mt-4">
        {error}
        <div className="mt-3">
          <Button onClick={() => navigate('/employees')} variant="primary">
            Back to Employees
          </Button>
        </div>
      </Alert>
    );
  }
  
  // If employee not found
  if (!employee) {
    return (
      <Alert variant="warning" className="mt-4">
        Employee not found
        <div className="mt-3">
          <Button onClick={() => navigate('/employees')} variant="primary">
            Back to Employees
          </Button>
        </div>
      </Alert>
    );
  }
  
  const totals = calculateTotals();
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{`${employee.FirstName} ${employee.LastName}`}</h2>
        <div>
          <Button
            variant="outline-primary"
            className="me-2"
            as={Link}
            to={`/employees/${id}/edit`}
          >
            Edit Employee
          </Button>
          <Button
            variant="outline-danger"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      </div>
      
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="details" title="Employee Details">
          <Card>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h5>Personal Information</h5>
                  <p><strong>Name:</strong> {employee.FirstName} {employee.LastName}</p>
                  <p><strong>Phone:</strong> {employee.Phone || 'Not provided'}</p>
                  <p><strong>Email:</strong> {employee.Email || 'Not provided'}</p>
                  
                  {(employee.Address || employee.City || employee.State || employee.ZipCode) && (
                    <>
                      <p><strong>Address:</strong></p>
                      <p>
                        {employee.Address}
                        <br />
                        {employee.City}, {employee.State} {employee.ZipCode}
                      </p>
                    </>
                  )}
                </Col>
                <Col md={6}>
                  <h5>Employment Information</h5>
                  <p>
                    <strong>Status:</strong>{' '}
                    <Badge bg={employee.IsActive ? 'success' : 'secondary'}>
                      {employee.IsActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </p>
                  <p><strong>Hourly Wage:</strong> {formatCurrency(employee.HourlyWage)}</p>
                  
                  <h5 className="mt-4">Summary</h5>
                  <p><strong>Total Regular Hours:</strong> {totals.regularHours.toFixed(2)}</p>
                  <p><strong>Total Overtime Hours:</strong> {totals.overtimeHours.toFixed(2)}</p>
                  <p><strong>Total Hours:</strong> {totals.totalHours.toFixed(2)}</p>
                  <p><strong>Total Earnings:</strong> {formatCurrency(totals.totalEarnings)}</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="timeEntries" title="Time Entries">
          <Card>
            <Card.Body>
              {timeEntries.length > 0 ? (
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Project</th>
                        <th>Regular Hours</th>
                        <th>Overtime Hours</th>
                        <th>Total Hours</th>
                        <th>Total Earnings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeEntries.map(entry => {
                        const regularEarnings = parseFloat(entry.RegularHours) * parseFloat(employee.HourlyWage);
                        const overtimeEarnings = parseFloat(entry.OvertimeHours || 0) * parseFloat(employee.HourlyWage) * 1.5;
                        const totalEarnings = regularEarnings + overtimeEarnings;
                        const totalHours = parseFloat(entry.RegularHours) + parseFloat(entry.OvertimeHours || 0);
                        
                        return (
                          <tr key={entry.TimeEntryId}>
                            <td>{new Date(entry.Date).toLocaleDateString()}</td>
                            <td>
                              <Link to={`/projects/${entry.ProjectId}`}>
                                {entry.ProjectName}
                              </Link>
                            </td>
                            <td>{entry.RegularHours}</td>
                            <td>{entry.OvertimeHours || '0.00'}</td>
                            <td>{totalHours.toFixed(2)}</td>
                            <td>{formatCurrency(totalEarnings)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="table-primary">
                        <td colSpan={2}><strong>Totals</strong></td>
                        <td><strong>{totals.regularHours.toFixed(2)}</strong></td>
                        <td><strong>{totals.overtimeHours.toFixed(2)}</strong></td>
                        <td><strong>{totals.totalHours.toFixed(2)}</strong></td>
                        <td><strong>{formatCurrency(totals.totalEarnings)}</strong></td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p>No time entries found for this employee.</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="projects" title="Assigned Projects">
          <Card>
            <Card.Body>
              {projects.length > 0 ? (
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Project Name</th>
                        <th>Client</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map(project => (
                        <tr key={project.ProjectId}>
                          <td>
                            <Link to={`/projects/${project.ProjectId}`}>
                              {project.ProjectName}
                            </Link>
                          </td>
                          <td>{project.ClientName}</td>
                          <td>
                            <Badge bg={
                              project.Status === 'Completed' ? 'success' :
                              project.Status === 'In Progress' ? 'primary' :
                              project.Status === 'On Hold' ? 'warning' :
                              project.Status === 'Cancelled' ? 'danger' :
                              'secondary'
                            }>
                              {project.Status}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              as={Link}
                              to={`/projects/${project.ProjectId}/time-entries?employeeId=${id}`}
                              variant="outline-primary"
                              size="sm"
                            >
                              Time Entries
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p>This employee is not assigned to any projects.</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
};

export default EmployeeDetail;
